import { AuthService } from '@adminprodigydesign/axis';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, of, shareReplay, tap } from 'rxjs';
import { environment } from '../../../environments/environment';

export type AddressSummary = {
  id: string;
  userAddressLinkId?: number | string;
  isDefault?: boolean;
  label: string;
  line1: string;
  line2?: string | null;
  city?: string | null;
  county?: string | null;
  postcode?: string | null;
  country?: string | null;
  raw?: any;
};

export type NewAddress = {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  county?: string;
  country: string;
  postalCode: string;
};

export type UserOrderDetailItem = {
  itemId?: number | string;
  itemName?: string;
  itemQuantity?: number | string;
  itemTotalGrossOriginal?: string | number;
  itemPrice?: string | number;
  extras?: Array<{
    name?: string;
    choice?: string;
    price?: string | number;
    [key: string]: any;
  }>;
  itemNote?: string | null;
  [key: string]: any;
};

export type UserOrderDetails = {
  id?: number | string;
  publicOrderCode?: string;
  orderStatus?: string;
  paymentStatus?: string;
  orderType?: string;
  productTotalOriginalGross?: string | number;
  orderTotalCurrentGross?: string | number;
  orderTotalCurrentReason?: string;
  createdAt?: string;
  createdAtLocal?: string;
  updatedAt?: string;
  updatedAtLocal?: string;
  comment?: string | null;
  tenantComment?: string | null;
  details?: UserOrderDetailItem[];
  [key: string]: any;
};

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private readonly apiUrl = environment.orderUrl;
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(
    private http: HttpClient,
    private auth: AuthService,
  ) {}

  viewAddresses(): Observable<any> {
    const form = new FormData();
    form.append('action', 'viewAddress');

    return this.http.post<any>(this.apiUrl, form, {
      withCredentials: true,
    });
  }

  insertAddress(addr: NewAddress): Observable<any> {
    const form = new FormData();
    form.append('action', 'insertAddress');
    form.append('foodOrderAddress[addressLine1]', addr.addressLine1);
    form.append('foodOrderAddress[addressLine2]', addr.addressLine2 ?? '');
    form.append('foodOrderAddress[city]', addr.city);
    form.append('foodOrderAddress[county]', addr.county ?? '');
    form.append('foodOrderAddress[country]', addr.country || 'UK');
    form.append('foodOrderAddress[postalCode]', addr.postalCode);

    return this.http.post<any>(this.apiUrl, form, {
      withCredentials: true,
    });
  }

  deleteAddressLink(linkId: number | string): Observable<any> {
    const form = new FormData();
    form.append('action', 'deleteAddressLink');
    form.append('linkId', String(linkId));

    return this.http.post<any>(this.apiUrl, form, {
      withCredentials: true,
    });
  }

  updateAddressLink(
    linkId: number | string,
    isDefault: 0 | 1,
  ): Observable<any> {
    const form = new FormData();
    form.append('action', 'updateAddressLink');
    form.append('linkId', String(linkId));
    form.append('isDefault', String(isDefault));

    return this.http.post<any>(this.apiUrl, form, {
      withCredentials: true,
    });
  }

  viewUserOrderHistory(): Observable<any> {
    const form = new FormData();
    form.append('action', 'viewUserOrderHistory');

    return this.http.post<any>(this.apiUrl, form, {
      withCredentials: true,
    });
  }

  viewUserOrderDetails(orderId: number): Observable<any> {
    const form = new FormData();
    form.append('action', 'viewUserOrderDetails');
    form.append('orderId', String(orderId));

    return this.http.post<any>(this.apiUrl, form, {
      withCredentials: true,
    });
  }

  setDefaultAddress(linkId: number | string): Observable<any> {
    return this.updateAddressLink(linkId, 1);
  }

  updateContactPhone(phoneNumber: string): Observable<any> {
    const form = new FormData();
    form.append('action', 'updateContactPhone');
    form.append('phoneNumber', String(phoneNumber).trim());

    return this.http.post<any>(this.baseUrl, form, {
      withCredentials: true,
    });
  }

  updateNameInfo(firstName?: string, lastName?: string): Observable<any> {
    const form = new FormData();
    form.append('action', 'updateNameInfo');
    form.append('firstName', String(firstName ?? '').trim());
    form.append('lastName', String(lastName ?? '').trim());

    return this.http.post<any>(this.baseUrl, form, {
      withCredentials: true,
    });
  }
}
